const React = require('react')
const { useState } = require('react')
const { useHistory } = require('react-router-dom')
const { UncontrolledTooltip, Table } = require('reactstrap')
const { useFormatBytes, useFormatTimestampShort, useFormatDateTime, useResponsive, BREAKPOINTS } = require('@hooks')
const { useNetworkConfig } = require('@contexts')

const { CopyButton } = require('@components/copyButton')
const { TransactionsPopover } = require('@components/popover/transaction')

require('@styles/components/transactions.css')

const TokenTransfersTable = props => {
    const { data, overviewData } = props
    const history = useHistory()
    const { network } = useNetworkConfig()
    const formatBytes = useFormatBytes()
    const formatTimestamp = useFormatTimestampShort()
    const formatDateTime = useFormatDateTime()
    const [showAge, setShowAge] = useState(true)

    const [activePreview, setActivePreview] = useState(null)
    const isMobile = useResponsive(BREAKPOINTS.large)

    return (
        <Table responsive style={isMobile ? { display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' } : {}}>
            <thead>
                <tr>
                    <th>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <div
                                id='preview-tooltip'
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <i
                                    className='fas fa-circle-question'
                                    style={{
                                        fontSize: '14px',
                                        color: 'var(--grey-color)'
                                    }}
                                />
                            </div>
                            <UncontrolledTooltip placement='top' target='preview-tooltip'>
                                See preview of transactions details.
                            </UncontrolledTooltip>
                        </div>
                    </th>
                    <th>Transaction Hash</th>
                    <th>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <span>Method</span>
                            <div
                                id='method-tooltip'
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <i
                                    className='fas fa-circle-question'
                                    style={{
                                        fontSize: '14px',
                                        color: 'var(--grey-color)'
                                    }}
                                />
                            </div>{' '}
                            <UncontrolledTooltip placement='top' target='method-tooltip'>
                                Function executed based on decoded input data. For unidentified functions, method ID is
                                displayed instead
                            </UncontrolledTooltip>
                        </div>
                    </th>
                    <th>Block</th>
                    <th>
                        <span
                            id='time-format-tooltip'
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => setShowAge(!showAge)}
                        >
                            {showAge ? 'Age' : 'Date Time (UTC)'}
                        </span>
                        <UncontrolledTooltip placement='top' target='time-format-tooltip'>
                            {showAge ? 'Click to show Datetime Format' : 'Click to show Age Format'}
                        </UncontrolledTooltip>
                    </th>
                    <th>From</th>
                    <th>To</th>
                    <th>{overviewData?.tokenType == 'ERC20' ? 'Amount' : 'Item'}</th>
                </tr>
            </thead>
            <tbody>
                {data?.length === 0 ? (
                    <tr>
                        <td colSpan='9' style={{ textAlign: 'center', padding: '40px 0', height: '400px' }}>
                            <div
                                style={{
                                    width: '72px',
                                    height: '72px',
                                    backgroundColor: 'rgba(255, 193, 7, 0.15)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 12px'
                                }}
                            >
                                <i
                                    className='fas fa-inbox'
                                    style={{
                                        fontSize: '24px',
                                        color: 'var(--grey-color)'
                                    }}
                                />
                            </div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>There are no matching entries</div>
                            <div style={{ color: 'var(--grey-color)', marginTop: '4px' }}>Please try again later</div>
                        </td>
                    </tr>
                ) : (
                    <>
                        {data?.map((transaction, index) => {
                            return (
                                <tr key={transaction.hash}>
                                    <td>
                                        <TransactionsPopover
                                            id={`preview-${transaction.hash}-${index}`}
                                            transaction={transaction}
                                            activePreview={activePreview}
                                            setActivePreview={setActivePreview}
                                        />
                                    </td>
                                    <td>
                                        {!transaction.status && (
                                            <>
                                                <div
                                                    className='tx-status-indicator'
                                                    style={{ color: 'var(--fail-color)' }}
                                                    id={`error-${transaction.hash}-${index}`}
                                                >
                                                    <i className='fas fa-times' />
                                                </div>
                                                <UncontrolledTooltip
                                                    placement='top'
                                                    target={`error-${transaction.hash}-${index}`}
                                                >
                                                    Error in Txn: execution reverted
                                                </UncontrolledTooltip>
                                            </>
                                        )}
                                        <span
                                            id={`tx-hash-${transaction.hash}-${index}`}
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() => history.push(`/${network.name}/tx/${transaction.hash}`)}
                                        >
                                            {formatBytes(transaction.hash, 12, 9, 0)}
                                        </span>
                                        <UncontrolledTooltip
                                            placement='top'
                                            target={`tx-hash-${transaction.hash}-${index}`}
                                        >
                                            {transaction.hash}
                                        </UncontrolledTooltip>
                                        &nbsp;
                                        <CopyButton
                                            id={`copy-tx-hash-${transaction.hash}-${index}`}
                                            data={transaction.hash}
                                            text={'Copy Transaction Hash'}
                                        />
                                    </td>
                                    <td>
                                        <span id={`method-id-${transaction.hash}-${index}`} className='method-id'>
                                            {transaction.functionName}
                                        </span>
                                        <UncontrolledTooltip
                                            placement='top'
                                            target={`method-id-${transaction.hash}-${index}`}
                                        >
                                            {transaction.functionName}
                                        </UncontrolledTooltip>
                                    </td>
                                    <td>
                                        <span
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() =>
                                                history.push(`/${network.name}/block/${transaction.blockNumber}`)
                                            }
                                        >
                                            {transaction.blockNumber}
                                        </span>
                                    </td>
                                    <td>
                                        {showAge ? (
                                            <>
                                                <span id={'age-' + transaction.hash}>
                                                    {formatTimestamp(transaction.blockTimestamp)}
                                                </span>
                                                <UncontrolledTooltip placement='top' target={`age-${transaction.hash}`}>
                                                    {formatDateTime(transaction.blockTimestamp)}
                                                </UncontrolledTooltip>
                                            </>
                                        ) : (
                                            <>
                                                <span id={'datetime-' + transaction.blockNumber}>
                                                    {formatDateTime(transaction.blockTimestamp)}
                                                </span>
                                                <UncontrolledTooltip
                                                    placement='top'
                                                    target={`datetime-${transaction.blockNumber}`}
                                                >
                                                    {formatTimestamp(transaction.blockTimestamp)}
                                                </UncontrolledTooltip>
                                            </>
                                        )}
                                    </td>
                                    <td>
                                        {transaction.transferFrom.type === 'SCA' && (
                                            <>
                                                <i
                                                    className='fas fa-file-alt'
                                                    style={{
                                                        fontSize: '14px',
                                                        color: 'var(--grey-color)'
                                                    }}
                                                />
                                                &nbsp;
                                            </>
                                        )}
                                        <span
                                            id={`from-${transaction.hash}-${index}`}
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() =>
                                                history.push(
                                                    `/${network.name}/address/${transaction.transferFrom.address}`
                                                )
                                            }
                                        >
                                            {formatBytes(transaction.transferFrom.address, 20, 7, 6)}
                                        </span>
                                        <UncontrolledTooltip
                                            placement='top'
                                            target={`from-${transaction.hash}-${index}`}
                                        >
                                            {transaction.transferFrom.address}
                                        </UncontrolledTooltip>
                                        &nbsp;
                                        <CopyButton
                                            id={`copy-from-${transaction.hash}-${index}`}
                                            data={transaction.transferFrom.address}
                                            text={'Copy Address'}
                                        />
                                    </td>
                                    <td>
                                        {transaction.transferTo.type === 'SCA' && (
                                            <>
                                                <i
                                                    className='fas fa-file-alt'
                                                    style={{
                                                        fontSize: '14px',
                                                        color: 'var(--grey-color)'
                                                    }}
                                                />
                                                &nbsp;
                                            </>
                                        )}
                                        <span
                                            id={`to-${transaction.hash}-${index}`}
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() =>
                                                history.push(
                                                    `/${network.name}/address/${transaction.transferTo.address}`
                                                )
                                            }
                                        >
                                            {formatBytes(transaction.transferTo.address, 20, 7, 6)}
                                        </span>
                                        <UncontrolledTooltip placement='top' target={`to-${transaction.hash}-${index}`}>
                                            {transaction.transferTo.address}
                                        </UncontrolledTooltip>
                                        &nbsp;
                                        <CopyButton
                                            id={`copy-to-${transaction.hash}-${index}`}
                                            data={transaction.transferTo.address}
                                            text={'Copy Address'}
                                        />
                                    </td>
                                    <td>
                                        {overviewData?.tokenType === 'ERC20' ? (
                                            <>
                                                {(
                                                    transaction.tokenValue /
                                                    10 ** overviewData?.tokenDecimals
                                                ).toLocaleString(undefined, {
                                                    minimumFractionDigits: 0,
                                                    maximumFractionDigits: overviewData?.tokenDecimals
                                                })}
                                            </>
                                        ) : (
                                            <>
                                                {overviewData?.tokenSymbol}&nbsp;#{transaction.tokenId}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </>
                )}
            </tbody>
        </Table>
    )
}

module.exports = { TokenTransfersTable }
